import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  ElementRef,
} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {Router, ActivatedRoute} from '@angular/router';
import {Store, select} from '@ngrx/store';
import {BehaviorSubject, Observable, Subscription, combineLatest, timer} from 'rxjs';
import {filter, map, take, withLatestFrom} from 'rxjs/operators';
import {Project} from 'src/app/core/model/project';
import {RewardCoin} from 'src/app/core/model/reward';
import {CurrentSkedTask, Subtask, SubtaskNote} from 'src/app/core/model/task';
import {User} from 'src/app/core/model/user';
import {selectActiveUser} from 'src/app/core/store/active-user/active-user.selector';
import {selectCurrentSked, selectCurrentSkedDetailTask} from 'src/app/core/store/current-sked/current-sked.selector';
import {GetLocationsAction} from 'src/app/core/store/locations/locations.action';
import {selectLocationsMap} from 'src/app/core/store/locations/locations.selector';
import {GetProjectsAction} from 'src/app/core/store/projects/projects.action';
import {selectProjectViewTaskId, selectProjectsMap} from 'src/app/core/store/projects/projects.selector';
import {GetCoinTypesAction} from 'src/app/core/store/rewards/rewards.action';
import {selectCoinTypesMap} from 'src/app/core/store/rewards/rewards.selector';
import {selectRouterParam} from 'src/app/core/store/router/router.selector';
import {LensSetting, LensSettingsOptions} from 'src/app/lenses/shared/model/lens-setting';
import {Location} from 'src/app/locations/shared/model/location';
import {MessageService} from 'src/app/services/message.service';
import {Sked} from '../shared/model/sked';
import {EMPTY_TASK_LOADING_STATE, TaskLoadingState} from '../shared/model/task-loading-state';
import {TaskStatusActions} from '../shared/task-status-actions';
import {TaskStatuses} from '../shared/task-statuses';
import {
  ClearCurrentSkedTaskAction,
  ClearCurrentSkedValueChosen,
  CurrentSkedChangeSubtaskStatusAction,
  CurrentSkedChangeTaskStatusAction,
  CurrentSkedCompleteTaskAction,
  CurrentSkedUpdateSubtaskMetricsAction,
  GetCurrentSkedTaskAction,
} from './../../core/store/current-sked/current-sked.action';
import {selectUsersMap} from 'src/app/core/store/users/users.selector';
import {MatDialog} from '@angular/material/dialog';
import {Alert} from 'src/app/core/model/alert';
import {AlertsService} from 'src/app/shared/services/alerts.service';
import {GetAlertsCountAction} from 'src/app/core/store/alerts/alerts.action';
import {
  registerCustomQuillLinkHandler,
  setCustomQuillLinkHandlerInstance,
  unregisterCustomQuillLinkHandler,
} from './utils/custom-quill-link-handler';
import {OphDrawerService} from './../../shared/design/oph-drawer/oph-drawer.service';

// Standalone component imports
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule} from '@angular/forms';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import {QuillModule} from 'ngx-quill';
import {SharedCurrentSkedModule} from 'src/app/shared/current-sked/shared-current-sked.module';
import {OphBackButtonComponent} from 'src/app/shared/design/oph-back-button/oph-back-button.component';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';
import {CurrentSkedTaskDetailDubaiThemeModule} from './dubai-theme/current-sked-task-detail-dubai-theme.module';
import {CurrentSkedTaskDetailMetricUpdateListComponent} from './metric-update-list/current-sked-task-detail-metric-update-list.component';
import {CurrentSkedNotesComponent} from './notes/current-sked-notes.component';
import {CurrentSkedTaskDetailStatusButtonComponent} from './status-button/current-sked-task-detail-status-button.component';
import {CurrentSkedTaskDetailInfoComponent} from './task-info/current-sked-task-detail-info.component';
import {CurrentSkedDetailUpdateButtonComponent} from './update-button/current-sked-detail-update-button.component';
import {OphDrawerModule} from './../../shared/design/oph-drawer/oph-drawer.module';
import {GetUserLensAction} from '../../core/store/lenses/lenses.action';
import {selectUserLens} from '../../core/store/lenses/lenses.selector';
import {AvailableTaskActions} from '../shared/model/available-task-actions';
import {CurrentSkedService} from '../shared/current-sked.service';
import {UserHasLensDirective} from './../../shared/directives/user-has-lens.directive';
import {TimezoneService} from '../../services/timezone.service';
import * as moment from 'moment-timezone';
import {CurrentSkedSkipConfirmationDialogComponent} from './skip-confirmation-dialog/current-sked-skip-confirmation-dialog.component';
import {MobileService} from 'src/app/core/page/mobile.service';

export interface UpdateSubtaskMetric {
  metricId: string;
  value: string;
  timestamp?: string;
}

const ANIMATION_DURATION_SHORT = 300;
const PAGE_SLIDE_DURATION = '200ms ease-out';

@Component({
  selector: 'current-sked-task-detail',
  templateUrl: './current-sked-task-detail.component.html',
  styleUrls: ['./current-sked-task-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  animations: [
    trigger('pageSlide', [
      state('enter', style({transform: 'translateX(0)', opacity: 1})),
      state('leave', style({transform: 'translateX(100%)', opacity: 0})),
      state('none', style({transform: 'translateX(0)', opacity: 1})),
      transition('void => enter', [
        style({transform: 'translateX(100%)', opacity: 0}),
        animate(PAGE_SLIDE_DURATION, style({transform: 'translateX(0)', opacity: 1})),
      ]),
      transition('void => none', []),
      transition('* => leave', animate(PAGE_SLIDE_DURATION)),
    ]),
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CurrentSkedTaskDetailDubaiThemeModule,
    OphLoadingModule,
    MatIconModule,
    CurrentSkedTaskDetailStatusButtonComponent,
    PipesModule,
    OphIconModule,
    MatExpansionModule,
    SharedCurrentSkedModule,
    OphBackButtonComponent,
    QuillModule,
    OphButtonModule,
    CurrentSkedTaskDetailMetricUpdateListComponent,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CurrentSkedTaskDetailInfoComponent,
    CurrentSkedDetailUpdateButtonComponent,
    CurrentSkedNotesComponent,
    OphDrawerModule,
    UserHasLensDirective,
  ],
})
export class CurrentSkedTaskDetailComponent implements OnInit, OnDestroy {
  readonly mobileService = inject(MobileService);
  @HostBinding('class') hostClasses = 'oph-feature-layout oph-feature-padding';
  @HostBinding('@pageSlide') animationState: 'enter' | 'leave' | 'none' = 'none';

  @Input() notInSked: boolean; // if this component is used in project viewer, this will be true

  @Output() notInSkedBack = new EventEmitter();
  @Output() notInSkedGet = new EventEmitter();

  @ViewChild('notesComponent') notesComponent: CurrentSkedNotesComponent;
  @ViewChild('mobileContainer') mobileContainer: ElementRef;
  @ViewChild('contentContainer') contentContainer: ElementRef;
  @ViewChild('mobileContentContainer') mobileContentContainer: ElementRef;

  metricTimerSubscription: Subscription;
  LensSettings = LensSettingsOptions;
  TaskStatusActions = TaskStatusActions;
  TaskStatuses = TaskStatuses;

  user$: Observable<User> = this.store$.pipe(select(selectActiveUser));
  usersMap$: Observable<Record<string, User>> = this.store$.pipe(select(selectUsersMap));
  userLens$: Observable<LensSetting> = this.store$.pipe(select(selectUserLens));
  sked$: Observable<Record<string, Sked>> = this.store$.pipe(select(selectCurrentSked));
  skedTimeStatus$: Observable<string> = this.store$.pipe(select(selectRouterParam('skedStatus')));
  skedIndex$: Observable<string> = this.store$.pipe(select(selectRouterParam('skedIndex')));
  skedId$: Observable<string> = this.store$.pipe(select(selectRouterParam('skedId')));
  taskId$: Observable<string> = this.store$.pipe(select(selectRouterParam('taskId')));
  storedTaskId$: Observable<string> = this.store$.pipe(select(selectProjectViewTaskId));
  task$: Observable<CurrentSkedTask> = this.store$.pipe(select(selectCurrentSkedDetailTask));
  projectsMap$: Observable<Record<string, Project>> = this.store$.pipe(select(selectProjectsMap));
  locationsMap$: Observable<Record<string, Location>> = this.store$.pipe(select(selectLocationsMap));

  ableToCompleteSubtaskIndex$: Observable<number> = this.observeLensSettings();

  availableTaskActions$: Observable<AvailableTaskActions> = this.observeTask();
  coinTypesMap$: Observable<Record<string, RewardCoin>> = this.store$.pipe(select(selectCoinTypesMap));
  areAllMetricsReadOnly$: Observable<boolean>;

  loading$ = new BehaviorSubject<TaskLoadingState>(EMPTY_TASK_LOADING_STATE);
  activeSubtask$ = new BehaviorSubject<Subtask>(null);
  activeSubtaskIndex$ = new BehaviorSubject<number>(null);
  mobileSubtaskDetailView$ = new BehaviorSubject<boolean>(false);
  taskHasOneSubtask$ = new BehaviorSubject<boolean>(false);
  taskStatuses = TaskStatuses;

  uploadProgress$ = new BehaviorSubject<{[key: number]: number}>({});
  imageLoading$ = new BehaviorSubject<{[key: number]: boolean}>({});
  reversedNotes$ = new BehaviorSubject<SubtaskNote[]>([]);
  taskExpired$ = new BehaviorSubject<boolean>(false);

  drawerService = inject(OphDrawerService);
  private resizeListener: () => void;

  isNotesDrawerOpen = false;
  uploadImage = false;

  constructor(
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store,
    private currentSkedService: CurrentSkedService,
    public dialog: MatDialog,
    private alertsService: AlertsService,
    private timezoneService: TimezoneService
  ) {
    if (this.currentSkedService.navigatingFromTaskList) {
      this.currentSkedService.navigatingFromTaskList = false;
      this.animationState = 'enter';
    } else {
      const nav = this.router.getCurrentNavigation();
      this.animationState = nav?.extras?.state?.['fromTaskList'] ? 'enter' : 'none';
    }
  }

  ngOnInit() {
    this.store$.dispatch(new GetUserLensAction({}));
    this.store$.dispatch(new GetProjectsAction({}));
    this.store$.dispatch(new GetLocationsAction({}));
    this.store$.dispatch(new GetCoinTypesAction({}));

    this.setInitialSubtask();
    this.getTask();

    setCustomQuillLinkHandlerInstance(this, 'drawer');
    registerCustomQuillLinkHandler();
    // Add resize listener to handle viewport changes
    this.resizeListener = () => {
      if (this.isNotesDrawerOpen) {
        this.toggleBodyScroll(!window.matchMedia('(min-width: 1001px)').matches);
      }
    };
    window.addEventListener('resize', this.resizeListener);
    this.areAllMetricsReadOnly$ = this.observeActiveSubtask();
  }

  ngOnDestroy(): void {
    this.store$.dispatch(new ClearCurrentSkedTaskAction());
    this.store$.dispatch(new ClearCurrentSkedValueChosen());

    if (this.metricTimerSubscription) {
      this.metricTimerSubscription.unsubscribe();
    }

    if (this.isNotesDrawerOpen) {
      this.toggleBodyScroll(true);
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }

    setCustomQuillLinkHandlerInstance(null);
    unregisterCustomQuillLinkHandler();
  }

  observeActiveSubtask(): Observable<boolean> {
    return this.activeSubtask$.pipe(
      filter(subtask => !!subtask),
      map(subtask => subtask.tracker?.metrics?.every(metric => metric.readOnly))
    );
  }

  observeTask(): Observable<AvailableTaskActions> {
    return this.task$.pipe(
      filter(task => !!task),
      withLatestFrom(this.user$, this.userLens$),
      map(([task, user, userLens]) => {
        if (!task || !userLens) return;
        return this.currentSkedService.getAvailableTaskActions(task, user, userLens, 'current');
      })
    );
  }

  private setActiveSubtaskWithAnimation(subtask: Subtask, index: number, mobile?: boolean) {
    const container = mobile ? this.mobileContentContainer?.nativeElement : this.contentContainer.nativeElement;

    // If mobile content container is not available (e.g., not in subtask detail view), fall back to mobile container
    if (mobile && !container && this.mobileContainer) {
      // If we're on mobile but the content container doesn't exist, we're probably in the subtask list view
      // In this case, we shouldn't animate, just set the subtask
      this.activeSubtask$.next(subtask);
      this.activeSubtaskIndex$.next(index);
      return;
    }

    if (!container) {
      // If no container is available, just set the subtask without animation
      this.activeSubtask$.next(subtask);
      this.activeSubtaskIndex$.next(index);
      return;
    }

    const currentIndex = this.activeSubtaskIndex$.value;
    const isMovingUp = currentIndex !== null && index < currentIndex;

    // Define animation classes based on mobile/desktop
    const fadeOutClass = mobile
      ? isMovingUp
        ? 'fade-out-right'
        : 'fade-out-left'
      : isMovingUp
        ? 'fade-out-up'
        : 'fade-out-down';

    const fadeInClass = mobile
      ? isMovingUp
        ? 'fade-in-right'
        : 'fade-in-left'
      : isMovingUp
        ? 'fade-in-up'
        : 'fade-in-down';

    // Remove any existing animation classes
    container.classList.remove(
      'fade-out-up',
      'fade-out-down',
      'fade-in-up',
      'fade-in-down',
      'fade-out-left',
      'fade-out-right',
      'fade-in-left',
      'fade-in-right'
    );

    // Add the appropriate fade out class
    container.classList.add(fadeOutClass);

    setTimeout(() => {
      this.activeSubtask$.next(subtask);
      this.activeSubtaskIndex$.next(index);

      // Remove fade out class and add fade in class
      container.classList.remove(fadeOutClass);
      container.classList.add(fadeInClass);
    }, ANIMATION_DURATION_SHORT);
  }

  setInitialSubtask() {
    this.task$
      .pipe(
        filter(task => !!task),
        take(1)
      )
      .subscribe(task => {
        let subtask = task.subtasks.find(subtask => !subtask.complete);

        // If no incomplete subtask found and there's a returnTo query param,
        // set subtask to the last subtask
        if (!subtask) {
          subtask = task.subtasks[task.subtasks.length - 1];
        }

        if (subtask) {
          this.activeSubtask$.next(subtask);
          this.activeSubtaskIndex$.next(task.subtasks.indexOf(subtask));
        }
      });
  }

  observeLensSettings(): Observable<number> {
    return combineLatest([this.task$, this.userLens$]).pipe(
      map(([task, settingsMap]) => {
        if (!task || !settingsMap) return;
        if (!task.authorizationObject?.isAuthorized) return -1;

        if (!settingsMap[LensSettingsOptions.CompleteSubtasksInAnyOrder]?.active) {
          let index: number;
          task.subtasks.forEach((subtask, i) => {
            if (subtask.complete) {
              index = i + 1;
            }
          });
          return index || 0;
        } else {
          return task.subtasks.length;
        }
      })
    );
  }

  getTask() {
    combineLatest([this.taskId$, this.storedTaskId$])
      .pipe(
        map(([id, storedId]) => id || storedId),
        filter(id => !!id),
        take(1)
      )
      .subscribe(taskId => {
        this.store$.dispatch(
          new GetCurrentSkedTaskAction({
            taskId,
            onSuccess: (task: CurrentSkedTask) => this.onGetTaskSuccess(task),
            onFailure: () => this.onGetTaskFailure(),
          })
        );
      });
  }

  onGetTaskSuccess(task: CurrentSkedTask) {
    if (task?.subtasks.length === 1) {
      this.taskHasOneSubtask$.next(true);
      this.mobileSubtaskDetailView$.next(true);
    }
    const isExpired = task?.endDateTime
      ? moment()
          .tz(this.timezoneService.getTimeZone())
          .isAfter(moment(task.endDateTime).tz(this.timezoneService.getTimeZone()))
      : false;
    this.taskExpired$.next(isExpired);
    this.loading$.next({...this.loading$.value, task: false, metricId: '', subtaskIndex: null, action: false});
  }

  onGetTaskFailure() {
    this.loading$.next({...this.loading$.value, task: false});
    this.messageService.add('There was a problem getting this task.');
    if (this.notInSked) {
      return this.notInSkedBack.emit();
    }
    timer(10).subscribe(() => this.navigateToProject());
  }

  onBack() {
    const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
    if (returnTo) {
      this.animationState = 'leave';
      setTimeout(() => this.router.navigate([returnTo]), 200);
      return;
    }
    if (this.notInSked) {
      return this.notInSkedBack.emit();
    }
    this.animationState = 'leave';
    setTimeout(() => this.navigateToProject(), 200);
  }

  private navigateToProject() {
    const projectId = this.route.snapshot.paramMap.get('projectId');
    if (projectId) {
      this.router.navigate(['current-sked', 'project', projectId]);
    } else {
      this.router.navigate(['current-sked', 'my-sked']);
    }
  }

  onSubtaskClick(subtask: Subtask, subtaskIndex: number, mobile?: boolean) {
    // If the incoming subtask index is already active, nothing should happen
    // This prevents clicking the same task and triggering the animation again
    // Since on mobile we have a different view for clicking the task and for viewing, we skip this check on mobile
    if (this.activeSubtaskIndex$.value === subtaskIndex && !mobile) {
      return;
    }

    this.isNotesDrawerOpen = false;
    this.setActiveSubtaskWithAnimation(subtask, subtaskIndex, mobile);
    if (mobile) {
      this.mobileSubtaskDetailView$.next(true);
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  }

  onUpdateTaskStatus(actionType: TaskStatusActions) {
    combineLatest([this.task$, this.skedId$])
      .pipe(take(1))
      .subscribe(([task, skedId]) => {
        if (actionType === TaskStatusActions.Complete) {
          this.loading$.next({...this.loading$.value, action: true});
          this.store$.dispatch(
            new CurrentSkedCompleteTaskAction({
              taskId: task._id,
              onSuccess: response => {
                this.onActionSuccess(actionType, response.task);
                this.alertsService.processAlerts(response.activities);
              },
              onFailure: () => this.onActionFailure(actionType),
            })
          );
        } else {
          this.store$.dispatch(
            new CurrentSkedChangeTaskStatusAction({
              id: task._id,
              actionType,
              onSuccess: task => {
                this.onActionSuccess(actionType, task);
              },
              onFailure: () => this.onActionFailure(actionType),
            })
          );
        }
      });
  }

  checkIfTaskComplete(task: CurrentSkedTask) {
    if (task?.status === this.taskStatuses.Completed) {
      if (this.notInSked) {
        return;
      }
      this.messageService.add('Task is complete', 'taskCompleted');
      this.navigateToProject();
    }
  }

  onActionSuccess(actionType: TaskStatusActions, task: CurrentSkedTask) {
    this.notInSkedGet.emit();
    this.checkIfTaskComplete(task);
    this.store$.dispatch(new GetAlertsCountAction());
    if (actionType === TaskStatusActions.Cancel) {
      this.navigateToProject();
    }
    this.loading$.next({...this.loading$.value, action: false});
  }

  onActionFailure(actionType: TaskStatusActions) {
    const statusText = this.getFailureStatusText(actionType);
    this.messageService.add(`There was a problem ${statusText} the task.`);
    this.loading$.next({...this.loading$.value, action: false});
  }

  getFailureStatusText(actionType: TaskStatusActions): string {
    switch (actionType) {
      case TaskStatusActions.Start:
        return 'starting';
      case TaskStatusActions.Pause:
        return 'pausing';
      case TaskStatusActions.Complete:
        return 'completing';
      case TaskStatusActions.Cancel:
        return 'skipping';
    }
  }

  onUpdateMetricValues(
    metrics: UpdateSubtaskMetric[],
    timestamp: string,
    subtask: Subtask,
    subtaskIndex: number,
    taskId: string,
    mobile: boolean
  ) {
    this.loading$.next({
      ...this.loading$.value,
      subtaskIndex,
      metricStatus: 'loading',
    });
    this.store$.dispatch(
      new CurrentSkedUpdateSubtaskMetricsAction({
        trackerId: subtask.trackerId,
        taskId,
        metrics,
        subtaskId: subtask._id,
        timestamp,
        taskExpired: this.taskExpired$.value,
        onSuccess: response => this.onUpdateMetricValueSuccess(response, mobile),
        onFailure: () => this.onUpdateMetricValueFailure(),
      })
    );
  }

  onUpdateMetricValueSuccess(response: {task: CurrentSkedTask; activities: Alert[]}, mobile: boolean) {
    const task = response.task;
    this.alertsService.processAlerts(response.activities);
    this.loading$.next({...this.loading$.value, metricStatus: 'success'});
    this.store$.dispatch(new GetAlertsCountAction());
    // Unsubscribe from the previous timer if it's still active
    if (this.metricTimerSubscription) {
      this.metricTimerSubscription.unsubscribe();
    }
    this.metricTimerSubscription = timer(400).subscribe(() => {
      this.loading$.next({...this.loading$.value, metricStatus: '', subtaskIndex: null});
      this.checkIfTaskComplete(task);

      if (this.activeSubtask$.value) {
        const subtaskIndex = task.subtasks.findIndex(subtask => !subtask.complete);
        if (subtaskIndex || subtaskIndex === 0) {
          const selectedSubtask = task.subtasks[subtaskIndex];
          this.setActiveSubtaskWithAnimation(selectedSubtask, subtaskIndex, mobile);
          this.updateReversedNotes(selectedSubtask);
        } else {
          this.messageService.add('Subtask was not found. Defaulting to first subtask.');
        }
      }
    });
  }

  onUpdateMetricValueFailure() {
    this.loading$.next({...this.loading$.value, metricStatus: '', subtaskIndex: null});
    this.messageService.add('There was a problem updating the metric value.');
  }

  onUpdateSubtaskStatus(complete: boolean, subtask: Subtask, subtaskIndex: number, mobile: boolean) {
    this.loading$.next({...this.loading$.value, subtaskIndex, checkbox: true});
    this.task$.pipe(take(1)).subscribe(task => {
      this.store$.dispatch(
        new CurrentSkedChangeSubtaskStatusAction({
          taskId: task._id,
          subtaskId: subtask._id,
          complete,
          onSuccess: (response: {task: CurrentSkedTask; activities: Alert[]}) => {
            this.onChangeSubtaskStatusSuccess(response.task, mobile);
            this.alertsService.processAlerts(response.activities);
            this.store$.dispatch(new GetAlertsCountAction());
          },
          onFailure: () => this.onChangeSubtaskStatusFailure(),
        })
      );
    });
  }

  onChangeSubtaskStatusSuccess(task: CurrentSkedTask, mobile: boolean) {
    if (task?.error) {
      this.messageService.add(task.error);
    }
    this.checkIfTaskComplete(task);
    this.loading$.next({...this.loading$.value, metricStatus: 'success', checkbox: false});
    timer(400).subscribe(() => {
      this.loading$.next({...this.loading$.value, subtaskIndex: null, checkbox: false, metricStatus: ''});
      if (this.activeSubtask$.value) {
        const updatedSubtask = task.subtasks.find(subtask => !subtask.complete);
        if (updatedSubtask) {
          this.setActiveSubtaskWithAnimation(updatedSubtask, task.subtasks.indexOf(updatedSubtask), mobile);
          this.updateReversedNotes(updatedSubtask);
        }
      }
    });
  }

  onChangeSubtaskStatusFailure() {
    this.messageService.add('There was a problem updating the subtask.');
    this.loading$.next({...this.loading$.value, subtaskIndex: null, checkbox: false});
  }

  private updateReversedNotes(subtask: Subtask) {
    this.reversedNotes$.next([...(subtask?.notes || [])].reverse());
  }

  onMobileSubtaskDetailBack() {
    if (this.taskHasOneSubtask$.value) {
      this.onBack();
    }
    this.mobileSubtaskDetailView$.next(false);
  }

  onNotesClick(uploadImage?: boolean) {
    this.uploadImage = uploadImage;
    this.isNotesDrawerOpen = true;

    // Only apply the delay on mobile to avoid the jump
    if (window.innerWidth <= 1000) {
      // Start the drawer animation first
      // Then prevent scrolling on mobile after a delay to avoid the jump
      setTimeout(() => {
        this.toggleBodyScroll(false);
      }, 300);
    } else {
      // On desktop, we can toggle immediately as there's no full-screen effect
      this.toggleBodyScroll(false);
    }

    // If we need to upload a photo and the notes component is already initialized,
    // trigger the upload directly
    if (uploadImage && this.notesComponent) {
      setTimeout(() => {
        this.notesComponent.onUploadPhotoClick();
      });
    }
  }

  onNotesClose() {
    this.isNotesDrawerOpen = false;
    this.uploadImage = false;

    // Re-enable scrolling
    this.toggleBodyScroll(true);
  }

  // Helper method to toggle body scroll
  private toggleBodyScroll(enable: boolean) {
    // Only apply on mobile devices
    if (window.innerWidth <= 1000) {
      if (enable) {
        // First restore the scroll position
        const scrollY = document.documentElement.style.getPropertyValue('--window-scroll-top') || '0px';

        // Then remove the class that disables scrolling
        document.body.classList.remove('notes-drawer-open');
        document.documentElement.style.removeProperty('--window-scroll-top');

        // Finally scroll to the saved position
        window.scrollTo(0, parseInt(scrollY || '0'));
      } else {
        // Save current scroll position before locking the scroll
        const scrollPosition = window.scrollY;
        document.documentElement.style.setProperty('--window-scroll-top', `${scrollPosition}px`);

        // Add the class that disables scrolling
        document.body.classList.add('notes-drawer-open');
      }
    }
  }

  onPostNoteSuccess(task: CurrentSkedTask) {
    const currentIndex = this.activeSubtaskIndex$.value;
    if (currentIndex !== null && currentIndex !== undefined) {
      this.activeSubtask$.next(task.subtasks[currentIndex]);
    }
  }

  onSkipTask() {
    const dialogRef = this.dialog.open(CurrentSkedSkipConfirmationDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      panelClass: 'oph-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loading$.next({...this.loading$.value, skip: true});
        this.onUpdateTaskStatus(TaskStatusActions.Cancel);
      }
    });
  }
}
